import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Image } from '../Image';
import { Button } from '../Button';

export interface CardModalItem {
  title?: string | null;
  imageUrl?: string | null;
}

export interface CardModalProps {
  open: boolean;
  onClose: () => void;
  /** When `null` the modal renders nothing (matches web `CardModal` behaviour). */
  item: CardModalItem | null;
  /** Chip text above the title — e.g. "Potential Hit", "Grail", "Chase". */
  label?: string;
  /** Bottom action button label. Defaults to "Back" (matches web). */
  closeLabel?: string;
}

export function CardModal({
  open,
  onClose,
  item,
  label,
  closeLabel = 'Back',
}: CardModalProps) {
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (item) setImageLoading(true);
  }, [item?.imageUrl]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    // Compensate for the disappearing vertical scrollbar so the page
    // doesn't jump horizontally when the modal opens.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const currentPad = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPad + scrollbarWidth}px`;
    }
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  if (!open || !item) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-stretch justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={label ?? item.title ?? 'Card'}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default backdrop-blur-[2px]"
        style={{ background: 'var(--co-CardModal-backdropTint, rgba(0,0,0,0.60))' }}
      />

      <div
        data-co-component="CardModal"
        className="
          relative flex w-full flex-col p-4
          h-[100dvh] rounded-none
          sm:h-auto sm:max-w-[500px] sm:[border-radius:var(--co-CardModal-borderRadius,28px)] sm:p-5
        "
        // Design Constructor surface: `CardModal`. Same theming pattern as
        // PackInfoCard — bg/border/blur/typography via `--co-CardModal-*`.
        // Defaults reproduce the previous GLASS_SURFACE wash, unmodified
        // deployments stay pixel-identical.
        style={{
          backgroundImage:
            'linear-gradient(' +
            'var(--co-CardModal-bgDirection, to bottom), ' +
            'color-mix(in srgb, var(--co-CardModal-bgFrom, rgba(255,255,255,0.20)) ' +
            'calc(var(--co-CardModal-bgOpacity, 1) * 100%), transparent) 0%, ' +
            'color-mix(in srgb, var(--co-CardModal-bgTo, rgba(153,153,153,0.20)) ' +
            'calc(var(--co-CardModal-bgOpacity, 1) * 100%), transparent) 100%)',
          backdropFilter: 'blur(var(--co-CardModal-glassBlur, 27px))',
          WebkitBackdropFilter: 'blur(var(--co-CardModal-glassBlur, 27px))',
          borderStyle: 'solid',
          borderWidth: 'var(--co-CardModal-borderWidth, 1px)',
          borderColor: 'var(--co-CardModal-borderColor, rgba(171,174,179,0.24))',
          fontFamily: 'var(--co-CardModal-fontFamily), Inter, system-ui, sans-serif',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {label && (
          <div className="flex justify-center">
            <span
              className="inline-flex items-center rounded-full border border-white/30 px-4 py-1.5 uppercase tracking-wide"
              style={{
                color: 'var(--co-CardModal-labelColor, #ffffff)',
                fontSize: 'var(--co-CardModal-labelSize, 14px)',
              }}
            >
              {label}
            </span>
          </div>
        )}

        <div
          className="
            mt-3 flex flex-1 min-h-0 flex-col items-center justify-center gap-3
            sm:flex-none sm:gap-4
          "
        >
          {item.title && (
            <h3
              className="text-center font-semibold leading-tight"
              style={{
                color: 'var(--co-CardModal-titleColor, #ffffff)',
                fontSize: 'var(--co-CardModal-titleSize, 26px)',
              }}
            >
              {item.title}
            </h3>
          )}

          <div
            className="
              relative w-full max-h-full min-h-0 aspect-[195/287]
              overflow-hidden rounded-[20px] bg-white/5
              sm:aspect-square sm:max-h-none
            "
          >
            {imageLoading && (
              <div className="absolute inset-0 animate-pulse bg-white/10" />
            )}
            <Image
              src={item.imageUrl ?? ''}
              alt={item.title ?? ''}
              className="absolute inset-0"
              imgClassName="absolute inset-0 h-full w-full object-contain pointer-events-none"
              onLoad={() => setImageLoading(false)}
            />
          </div>
        </div>

        <div className="mt-4 pb-[env(safe-area-inset-bottom)] sm:pb-0">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={onClose}
            className="min-h-[56px] text-[17px]"
          >
            {closeLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
